'use client';

import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import * as React from 'react';
import { useImperativeHandle } from 'react';
import TimePeriodSelect from './time-period-select';
import { TimePickerInput } from './time-picker-input';
import { Period } from './time-picker-utils';

type Granularity = 'day' | 'hour' | 'minute' | 'second';

interface TimePickerProps {
    date?: Date;
    setDate?: (date: Date | undefined) => void;
    hourCycle?: 12 | 24;
    /**
     * Determines the smallest unit that is displayed in the datetime picker.
     * Default is 'second'.
     * */
    granularity?: Granularity;
    className?: string;
}

interface TimePickerRef {
    minuteRef: HTMLInputElement | null;
    hourRef: HTMLInputElement | null;
    secondRef: HTMLInputElement | null;
}

const TimePicker = React.forwardRef<TimePickerRef, TimePickerProps>(
    (
        { date, setDate, hourCycle = 24, granularity = 'second', className },
        ref
    ) => {
        const minuteRef = React.useRef<HTMLInputElement>(null);
        const hourRef = React.useRef<HTMLInputElement>(null);
        const secondRef = React.useRef<HTMLInputElement>(null);
        const periodRef = React.useRef<HTMLButtonElement>(null);
        const [period, setPeriod] = React.useState<Period>(
            date && date.getHours() >= 12 ? 'PM' : 'AM'
        );

        useImperativeHandle(
            ref,
            () => ({
                minuteRef: minuteRef.current,
                hourRef: hourRef.current,
                secondRef: secondRef.current,
                periodRef: periodRef.current,
            }),
            [minuteRef, hourRef, secondRef]
        );

        return (
            <div
                className={cn(
                    'flex items-center justify-center gap-2',
                    className
                )}
            >
                <label
                    htmlFor='datetime-picker-hour-input'
                    className='cursor-pointer'
                >
                    <Clock className='mr-2 h-4 w-4' />
                </label>
                <TimePickerInput
                    picker={hourCycle === 24 ? 'hours' : '12hours'}
                    date={date}
                    id='datetime-picker-hour-input'
                    setDate={setDate}
                    ref={hourRef}
                    period={period}
                    onRightFocus={() => minuteRef.current?.focus()}
                />
                {(granularity === 'minute' || granularity === 'second') && (
                    <>
                        :
                        <TimePickerInput
                            picker='minutes'
                            date={date}
                            setDate={setDate}
                            ref={minuteRef}
                            onLeftFocus={() => hourRef.current?.focus()}
                            onRightFocus={() => secondRef.current?.focus()}
                        />
                    </>
                )}
                {granularity === 'second' && (
                    <>
                        :
                        <TimePickerInput
                            picker='seconds'
                            date={date}
                            setDate={setDate}
                            ref={secondRef}
                            onLeftFocus={() => minuteRef.current?.focus()}
                            onRightFocus={() => periodRef.current?.focus()}
                        />
                    </>
                )}
                {hourCycle === 12 && (
                    <div className='grid gap-1 text-center'>
                        <TimePeriodSelect
                            period={period}
                            setPeriod={setPeriod}
                            date={date}
                            onDateChange={(date) => {
                                setDate?.(date);
                                if (date && date?.getHours() >= 12) {
                                    setPeriod('PM');
                                } else {
                                    setPeriod('AM');
                                }
                            }}
                            ref={periodRef}
                            onLeftFocus={() => secondRef.current?.focus()}
                        />
                    </div>
                )}
            </div>
        );
    }
);
TimePicker.displayName = 'TimePicker';
export default TimePicker;
